import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { inventoryAttribute } from "@/features/provider-migration/shared";
import type { ProviderMigrationInventory } from "@/hooks/use-provider-migration";
import type { ApiProviderMigrationInventoryItem } from "@/lib/api";

/**
 * Provider migration, import review — "Canary source review".
 *
 * A treasury operator or reviewer picks exactly ONE source vault from the sealed provider
 * inventory already read by the page's summary hook, and the page scopes the provider's own
 * asset rows for that vault. This is inspection only:
 *
 *  - The selection is local component state. It is not a plan, not an approval, and it is never
 *    persisted, submitted or sent to any endpoint. The only network call this section can make is
 *    the page's existing read-only refresh.
 *  - Nothing here asserts canary readiness. The approved plan, the value/fee cap, verified
 *    ownership of the destination wallet, independent human approvals and finality are all
 *    required before a canary can run, and this review records none of them.
 *  - Amounts stay the exact strings the provider reported. They are never parsed to a number,
 *    summed, converted, truncated or re-formatted, so an 18-decimal balance is not rounded on the
 *    way to the screen and a value too long to display in full is "Not reported" rather than a
 *    shorter number the provider never sent. A value the provider did not report is "Not
 *    reported", never `0`.
 *  - `assetId` is shown as the provider's own asset identifier under the "Provider asset" column.
 *    No canonical chain, network, contract or scale is inferred from it.
 */

/**
 * The permanent, frontend-owned reason a canary cannot be executed from this page. It is a
 * capability statement about this screen, not a backend or provider error code.
 */
export const CANARY_EXECUTION_UNAVAILABLE_TEXT = "Canary execution is unavailable";
export const CANARY_EXECUTION_UNAVAILABLE_REASON = "CANARY_EXECUTION_UNAVAILABLE";

/** Why the three sealed categories cannot be reviewed as one selectable snapshot. */
export type SourceReviewBlockedReason =
  | "SOURCE_CATEGORY_UNAVAILABLE"
  | "SNAPSHOT_ID_MISSING"
  | "SNAPSHOT_ID_MISMATCH"
  | "SOURCE_READ_TRUNCATED"
  | "FRESHNESS_NOT_REPORTED"
  | "SNAPSHOT_NOT_FRESH";

const BLOCKED_COPY: Record<SourceReviewBlockedReason, string> = {
  SOURCE_CATEGORY_UNAVAILABLE:
    "One of the three sealed inventory categories could not be read, so there is no single complete snapshot to review.",
  SNAPSHOT_ID_MISSING:
    "The sealed read carries no snapshot reference, so source rows cannot be tied to one reviewable snapshot.",
  SNAPSHOT_ID_MISMATCH:
    "The three inventory categories come from different sealed snapshots. They are never presented as one source; refresh to read them together.",
  SOURCE_READ_TRUNCATED:
    "At least one category reached the read cap, so what was read is not the complete sealed inventory.",
  FRESHNESS_NOT_REPORTED:
    "At least one category did not report a freshness time, so the age of this evidence is unknown.",
  SNAPSHOT_NOT_FRESH:
    "The sealed snapshot is past the freshness it reported, so it may no longer reflect the provider.",
};

/**
 * A full ISO-8601 instant, captured field by field so the calendar itself can be validated. A bare
 * date, a partial time or an open-ended value is not a freshness claim and is rejected.
 */
const FULL_ISO_INSTANT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;

/**
 * `setTimeout` above this overflows a 32-bit delay, so an expiry further away than this is waited
 * for in bounded steps rather than not waited for at all.
 */
export const MAX_TIMER_MS = 2 ** 31 - 1;

/** The longest provider-reported value accepted as itself; anything longer is not that value. */
const MAX_REPORTED_LENGTH = 256;

/** A plain decimal string: digits with an optional fractional part. Nothing else is an amount. */
const REPORTED_DECIMAL = /^\d+(?:\.\d+)?$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * The exact string a value is when it is a non-blank string, and null otherwise.
 *
 * Identity is never trimmed. `snapshotId` and `vaultAccountId`/`providerResourceId` are compared and
 * joined exactly as the provider and the gateway sent them, so two references that differ only by
 * surrounding whitespace are two different references and never joined into one. Blankness is the
 * one thing a trim is allowed to decide.
 */
function rawIdentity(value: unknown, maximumLength = Number.POSITIVE_INFINITY): string | null {
  if (typeof value !== "string") return null;
  return value.trim().length > 0 && value.length <= maximumLength ? value : null;
}

/**
 * One provider-reported amount, exactly as the provider sent it.
 *
 * `inventoryAttribute` trims and caps its value at 256 characters, which is right for a display
 * label and wrong for a balance: a longer digit string would be silently shown as a shorter,
 * different amount. Here a malformed, overlong or non-string value is "not reported" instead, and a
 * valid bounded decimal is returned verbatim — never parsed to a float, rounded, summed or
 * reformatted, so an 18-decimal balance survives the trip to the screen and a value beyond
 * `Number.MAX_SAFE_INTEGER` is not the float beside it.
 */
export function reportedAmount(item: ApiProviderMigrationInventoryItem, key: string): string | null {
  const value = item.attributes?.[key];
  if (typeof value !== "string") return null;
  if (value.length === 0 || value.length > MAX_REPORTED_LENGTH) return null;
  return REPORTED_DECIMAL.test(value) ? value : null;
}

/**
 * Epoch ms for a full ISO-8601 instant, or null when the value is absent, malformed, or an invalid
 * calendar date or time.
 *
 * The regex and `Date.parse` are not enough on their own: `Date.parse("2027-02-30T00:00:00Z")`
 * accepts the string and rolls it forward to 2027-03-02, so an impossible date would be read as a
 * real, later freshness claim. Every field is range-checked first, including the leap-year rule and
 * the offset.
 */
export function parseFullIsoInstant(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const match = FULL_ISO_INSTANT.exec(trimmed);
  if (match === null) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offset = match[7];

  if (month < 1 || month > 12) return null;
  const maxDay = month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
  if (day < 1 || day > maxDay) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  if (offset !== "Z") {
    const offsetHour = Number(offset.slice(1, 3));
    const offsetMinute = Number(offset.slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) return null;
  }

  const ms = Date.parse(trimmed);
  return Number.isNaN(ms) ? null : ms;
}

export interface SourceReviewReadiness {
  /** True only when all three categories are one complete, fresh, single-snapshot read. */
  selectable: boolean;
  blocked: SourceReviewBlockedReason | null;
  snapshotId: string | null;
  /** Earliest `freshUntil` across the categories (epoch ms); null unless selectable. */
  earliestExpiry: number | null;
}

/**
 * The gate on source selection. Every condition is required at once: exactly three categories
 * present, one shared non-empty snapshot id compared exactly as sent, no truncation in any
 * category, and a full-ISO freshness instant that is a real calendar date still in the future for
 * each category. A snapshot whose age is unknown is not treated as fresh.
 */
export function sourceReviewReadiness(
  categories: ReadonlyArray<ProviderMigrationInventory | null | undefined>,
  now: number,
): SourceReviewReadiness {
  const blocked = (reason: SourceReviewBlockedReason): SourceReviewReadiness => ({
    selectable: false,
    blocked: reason,
    snapshotId: null,
    earliestExpiry: null,
  });

  const present = categories.filter((category): category is ProviderMigrationInventory => Boolean(category));
  // Exactly the three sealed categories, read together. A shorter or longer list is not that read,
  // so it is refused rather than treated as a complete snapshot (an empty list is never a success).
  if (categories.length !== 3 || present.length !== categories.length) {
    return blocked("SOURCE_CATEGORY_UNAVAILABLE");
  }

  const snapshotIds = present.map((category) => rawIdentity(category.snapshotId));
  if (snapshotIds.some((id) => id === null)) return blocked("SNAPSHOT_ID_MISSING");
  if (new Set(snapshotIds).size !== 1) return blocked("SNAPSHOT_ID_MISMATCH");

  if (present.some((category) => category.truncated)) return blocked("SOURCE_READ_TRUNCATED");

  const expiries = present.map((category) => parseFullIsoInstant(category.freshUntil));
  if (expiries.some((expiry) => expiry === null)) return blocked("FRESHNESS_NOT_REPORTED");

  const times = expiries as number[];
  if (times.some((expiry) => expiry <= now)) return blocked("SNAPSHOT_NOT_FRESH");

  return {
    selectable: true,
    blocked: null,
    snapshotId: snapshotIds[0] as string,
    earliestExpiry: Math.min(...times),
  };
}

/** A vault's option label: its reported name plus its provider reference, both bounded strings. */
export function sourceVaultLabel(vault: ApiProviderMigrationInventoryItem): string {
  const name = inventoryAttribute(vault, "name");
  return name ? `${name} (${vault.providerResourceId})` : vault.providerResourceId;
}

/**
 * The source table's columns, in one place: each header is also copied onto its cells as
 * `data-label`, which is what the design system's `.data-table` prints as the per-value label when
 * the table stacks into cards under 640px (globals.css). One list, so a header and its phone label
 * cannot drift apart.
 */
const SOURCE_COLUMNS = ["Provider reference", "Provider asset", "Total", "Available"] as const;

/** A reported decimal string exactly as the provider sent it. Never a number, never `0`. */
function ReportedAmount({ value }: { value: string | null }) {
  if (value === null) return <span className="text-subtle">Not reported</span>;
  return <span className="break-all font-mono text-xs tabular-nums">{value}</span>;
}

/** An attribute the provider did not report for this row: unavailable, not zero. */
function Unreported({ label }: { label: string }) {
  return <span className="text-subtle">{label}</span>;
}

export interface CanarySourceReviewProps {
  /** The sealed VAULT_ACCOUNTS read, or null when it could not be read. */
  vaults: ProviderMigrationInventory | null;
  /** The sealed ASSET_WALLETS read, or null when it could not be read. */
  assetWallets: ProviderMigrationInventory | null;
  /** The sealed VAULT_ADDRESSES read. Required for the snapshot gate; it holds no balances. */
  vaultAddresses: ProviderMigrationInventory | null;
  /** The page's existing read-only refresh. */
  onRefresh: () => void;
}

export function CanarySourceReview({ vaults, assetWallets, vaultAddresses, onRefresh }: CanarySourceReviewProps) {
  // `clock` is only a re-render tick. Readiness is always evaluated against the current time (see
  // below), so a snapshot that arrives already past its own reported freshUntil is refused on the
  // frame it arrives, instead of staying selectable until some timer happens to fire.
  const [clock, setClock] = useState(() => Date.now());
  const [selection, setSelection] = useState<{ snapshotId: string; vaultId: string } | null>(null);

  // Deliberately not memoised on the props alone: a memo would answer with the clock as it was
  // when the render first ran, which is exactly the stale-frame bug this guards against. The
  // computation is a handful of comparisons over three small arrays.
  const readiness = sourceReviewReadiness([vaults, assetWallets, vaultAddresses], Date.now());

  const vaultRows = vaults?.items;
  const walletRows = assetWallets?.items;
  const vaultIds = useMemo(() => new Set((vaultRows ?? []).map((vault) => vault.providerResourceId)), [vaultRows]);

  // One bounded timer at the earliest reported expiry, so a page left open stops offering a
  // selection the moment its snapshot goes stale. The wait is capped at `MAX_TIMER_MS` (a larger
  // delay overflows a 32-bit timer and would fire immediately) and the effect re-arms on every
  // tick, so an expiry further away than that cap still arrives eventually. It re-renders this
  // component only: it never polls, re-reads or retries anything.
  useEffect(() => {
    if (readiness.earliestExpiry === null) return;
    const remaining = readiness.earliestExpiry - Date.now();
    const delay = Math.min(Math.max(0, remaining), MAX_TIMER_MS - 1) + 1;
    const timer = window.setTimeout(() => setClock(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [readiness.earliestExpiry, clock]);

  // Selection is singular and never outlives the read that justified it: a new snapshot id, a
  // read that stops being reviewable, or a vault that is no longer in the snapshot all drop it.
  useEffect(() => {
    setSelection((current) => {
      if (current === null) return current;
      if (!readiness.selectable) return null;
      if (readiness.snapshotId !== current.snapshotId) return null;
      if (!vaultIds.has(current.vaultId)) return null;
      return current;
    });
  }, [readiness.selectable, readiness.snapshotId, vaultIds]);

  // Guarded again at render, so a stale selection can never paint one frame of rows from a
  // snapshot that has already been replaced.
  const selectedVaultId =
    readiness.selectable &&
    selection !== null &&
    selection.snapshotId === readiness.snapshotId &&
    vaultIds.has(selection.vaultId)
      ? selection.vaultId
      : null;

  const selectedVault = selectedVaultId === null ? null : (vaultRows ?? []).find((vault) => vault.providerResourceId === selectedVaultId) ?? null;

  const sourceRows = useMemo(() => {
    if (selectedVaultId === null) return [];
    // The join is on the raw references: a row whose vault reference differs from the vault's own
    // id by so much as a space is a different reference and is not that vault's row.
    return (walletRows ?? []).filter((row) => rawIdentity(row.attributes?.vaultAccountId) === selectedVaultId);
  }, [selectedVaultId, walletRows]);

  // Source rows that cannot be attributed to a vault in this snapshot are counted so their
  // absence is visible. They are never folded into a vault's rows, and their missing balances
  // are never reported as zero.
  const brokenReferences = useMemo(() => {
    let unlinked = 0;
    let orphaned = 0;
    for (const row of walletRows ?? []) {
      const vaultAccountId = rawIdentity(row.attributes?.vaultAccountId);
      if (vaultAccountId === null) unlinked += 1;
      else if (!vaultIds.has(vaultAccountId)) orphaned += 1;
    }
    return { unlinked, orphaned };
  }, [walletRows, vaultIds]);

  return (
    <section aria-labelledby="canary-source-heading" className="space-y-4">
      <div className="max-w-3xl">
        <h2 id="canary-source-heading" className="mb-1 text-base font-bold text-ink">
          Canary source review
        </h2>
        <p className="text-sm text-muted">
          Choose one source vault to review the provider balances a canary wave would draw from. Selecting a vault
          scopes this read-only view; it records no plan, cap or approval, and it cannot start, approve, sign or
          dispatch anything.
        </p>
      </div>

      {!readiness.selectable && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-line bg-surface px-4 py-4">
          <div>
            <p className="text-sm font-semibold text-ink">Source review is not available for this read</p>
            <p className="mt-1 max-w-3xl text-sm text-muted">
              {readiness.blocked === null
                ? "This read cannot be reviewed as one complete source snapshot."
                : BLOCKED_COPY[readiness.blocked]}
            </p>
          </div>
          {/* Same read-only refresh as the page header action, but labelled distinctly: the
              header button already carries the page-level "Refresh" name, and two controls with
              one accessible name would be ambiguous to a screen reader and to the DOM. */}
          <Button variant="secondary" size="sm" onClick={onRefresh}>
            Refresh inventory
          </Button>
        </div>
      )}

      {readiness.selectable && readiness.snapshotId !== null && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <div className="flex w-full min-w-0 flex-col gap-1 sm:w-auto">
              <label htmlFor="canary-source-vault" className="text-xs font-semibold text-muted">
                Source vault
              </label>
              <select
                id="canary-source-vault"
                value={selectedVaultId ?? ""}
                onChange={(event) => {
                  const vaultId = event.target.value;
                  if (!vaultId) {
                    setSelection(null);
                    return;
                  }
                  setSelection(readiness.snapshotId === null ? null : { snapshotId: readiness.snapshotId, vaultId });
                }}
                className="w-full min-w-0 max-w-sm rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary-soft"
              >
                <option value="">Select a source vault</option>
                {(vaultRows ?? []).map((vault) => (
                  <option key={vault.providerResourceId} value={vault.providerResourceId}>
                    {sourceVaultLabel(vault)}
                  </option>
                ))}
              </select>
            </div>
            <span className="flex flex-wrap items-center gap-2 pb-2 text-xs text-muted">
              Snapshot <IdentifierValue value={readiness.snapshotId} />
              <Badge variant="success">Fresh as reported</Badge>
            </span>
          </div>

          {(vaultRows ?? []).length === 0 ? (
            <EmptyState
              card
              title="No vault accounts in this sealed snapshot"
              description="The provider inventory this read covers contains no vault accounts, so there is no source vault to review. That is an absence of source rows, not an empty or funded vault."
            />
          ) : null}

          {selectedVaultId !== null && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink">
                <span className="break-all font-semibold">
                  {selectedVault === null ? "Source vault" : (inventoryAttribute(selectedVault, "name") ?? "Unnamed source vault")}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-muted">
                  Provider reference <IdentifierValue value={selectedVaultId} />
                </span>
              </div>

              {sourceRows.length === 0 ? (
                <EmptyState
                  card
                  title="No source asset rows for this vault"
                  description="The sealed snapshot reports no asset wallet rows for this vault. Nothing is reported for it here, which is not a zero balance and not evidence that the vault is empty."
                />
              ) : (
                <>
                  <TableWrapper>
                    <Table aria-label="Canary source assets">
                      <thead>
                        <tr>
                          {SOURCE_COLUMNS.map((column) => (
                            <th key={column} scope="col">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sourceRows.map((row) => (
                          <tr key={`${row.resourceType}:${row.providerResourceId}`}>
                            <td data-label={SOURCE_COLUMNS[0]}>
                              <IdentifierValue value={row.providerResourceId} />
                            </td>
                            <td className="break-all" data-label={SOURCE_COLUMNS[1]}>
                              {rawIdentity(row.attributes?.assetId, MAX_REPORTED_LENGTH) ?? <Unreported label="Unavailable" />}
                            </td>
                            <td data-label={SOURCE_COLUMNS[2]}>
                              <ReportedAmount value={reportedAmount(row, "total")} />
                            </td>
                            <td data-label={SOURCE_COLUMNS[3]}>
                              <ReportedAmount value={reportedAmount(row, "available")} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrapper>
                  <p className="text-xs text-muted">
                    Provider asset values are the provider's own asset identifiers, exactly as reported. No canonical
                    chain, network, contract or scale is derived from them.
                  </p>
                </>
              )}

              {(brokenReferences.unlinked > 0 || brokenReferences.orphaned > 0) && (
                <ul className="space-y-1 text-xs text-muted">
                  {brokenReferences.unlinked > 0 && (
                    <li>
                      {brokenReferences.unlinked === 1
                        ? "1 source asset row has no vault reference, so it cannot be attributed to any vault and is not counted under one."
                        : `${brokenReferences.unlinked} source asset rows have no vault reference, so they cannot be attributed to any vault and are not counted under one.`}
                    </li>
                  )}
                  {brokenReferences.orphaned > 0 && (
                    <li>
                      {brokenReferences.orphaned === 1
                        ? "1 source asset row references a vault that is not in this snapshot; it is unavailable here and is not counted under any vault."
                        : `${brokenReferences.orphaned} source asset rows reference a vault that is not in this snapshot; they are unavailable here and are not counted under any vault.`}
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <NotAvailableState
        title={CANARY_EXECUTION_UNAVAILABLE_TEXT}
        description="A canary requires an approved plan with a value and fee cap, verified destination ownership and independent human approvals. Its outcome then needs independent finalized transfer evidence. Source review records none of these prerequisites or outcomes."
        reason={CANARY_EXECUTION_UNAVAILABLE_REASON}
      />
    </section>
  );
}
